<script setup lang="ts">
/**
 * How long the food took.
 *
 * `projectTickets` has always computed the fire-to-bump time; the kitchen board
 * drops it, because a cook does not need it and had nowhere to see it. Same
 * projection, different filter.
 *
 * PERCENTILES, NOT AN AVERAGE. A mean service time is dragged around by the one
 * ticket somebody left on the rail, and it is the tail a guest experiences: the
 * table waiting twenty minutes does not feel the median.
 */

import StatTile from "./StatTile.vue";
import type { KitchenTimings } from "../api";
import { clockTime, serviceTime } from "../format";

defineProps<{
  timings: KitchenTimings;
  timezone: string;
  locale: string;
  t: (key: string) => string;
}>();
</script>

<template>
  <section>
    <div class="gf-tiles">
      <StatTile
        :label="t('kitchen.median')"
        :value="serviceTime(timings.medianServiceSeconds)"
        :hint="t('kitchen.whyPercentile')"
      />
      <StatTile :label="t('kitchen.p90')" :value="serviceTime(timings.p90ServiceSeconds)" />
      <StatTile :label="t('kitchen.tickets')" :value="String(timings.ticketCount)" />
      <StatTile
        :label="t('kitchen.served')"
        :value="`${String(timings.servedCount)} ${t('common.of')} ${String(timings.ticketCount)}`"
      />
      <StatTile
        :label="t('kitchen.late')"
        :value="String(timings.late.length)"
        :tone="timings.late.length > 0 ? 'warn' : 'plain'"
      />
    </div>

    <div class="gf-columns">
      <div class="gf-panel">
        <h2 class="gf-panel__title">{{ t("kitchen.station") }}</h2>
        <table class="gf-table">
          <thead>
            <tr>
              <th scope="col">{{ t("kitchen.station") }}</th>
              <th scope="col" class="gf-num">{{ t("kitchen.tickets") }}</th>
              <th scope="col" class="gf-num">{{ t("kitchen.median") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="station in timings.byStation" :key="station.stationId">
              <th scope="row">{{ station.stationName ?? station.stationId }}</th>
              <td class="gf-num">{{ station.ticketCount }}</td>
              <td class="gf-num">{{ serviceTime(station.medianServiceSeconds) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="gf-panel">
        <h2 class="gf-panel__title">{{ t("kitchen.late") }}</h2>
        <p v-if="timings.late.length === 0" class="gf-quiet">{{ t("common.empty") }}</p>
        <table v-else class="gf-table">
          <thead>
            <tr>
              <th scope="col">{{ t("checks.table") }}</th>
              <th scope="col">{{ t("kitchen.station") }}</th>
              <th scope="col">{{ t("common.time") }}</th>
              <th scope="col" class="gf-num">{{ t("kitchen.median") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in timings.late" :key="ticket.ticketId">
              <th scope="row">{{ ticket.tableName ?? (ticket.tableRef || "—") }}</th>
              <td>{{ ticket.stationName ?? ticket.stationId }}</td>
              <td>{{ clockTime(ticket.firedAt, timezone, locale) }}</td>
              <td class="gf-num">{{ serviceTime(ticket.serviceSeconds) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
